import React, { useState, useEffect, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  CircularProgress,
  Link,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { isTrialPlanName } from "../../components/PlansManager/PlanTrialPolicy";

import SubscriptionModal from "../../components/SubscriptionModal";
import InvoiceBinanceCheckoutDialog from "../../components/InvoiceBinanceCheckoutDialog";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Función helper para obtener el símbolo de la moneda
const getCurrencySymbol = (currency) => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    BRL: 'R$',
    MXN: '$',
    ARS: '$',
    CLP: '$',
    COP: '$',
    PEN: 'S/'
  };
  return symbols[currency] || '$';
};

const t = (key, options) => i18n.t(`financial.subscription.${key}`, options);

// Características que se muestran en cada tarjeta, en el orden de la escala de
// planes: lo incluido con check y lo que no, atenuado, para que se vea la diferencia.
const getPlanFeatures = (plan, locale) => {
  const fmt = (n) => Number(n || 0).toLocaleString(locale);
  const campaignsLabel = Number(plan.maxCampaignContacts) > 0 && Number(plan.maxCampaignsPerDay) > 0
    ? t("featureCampaignsLimit", { contacts: fmt(plan.maxCampaignContacts), perDay: fmt(plan.maxCampaignsPerDay) })
    : t("featureCampaigns");
  const groupsLabel = Number(plan.maxWhatsappGroupCampaigns) > 0 && Number(plan.maxWhatsappGroupsPerCampaign) > 0
    ? t("featureGroupCampaignsLimit", { active: fmt(plan.maxWhatsappGroupCampaigns), groups: fmt(plan.maxWhatsappGroupsPerCampaign) })
    : t("featureGroupCampaigns");
  return [
    { key: "users", included: true, label: t("users", { count: plan.users }) },
    { key: "connections", included: true, label: t("connections", { count: plan.connections }) },
    { key: "queues", included: true, label: t("queues", { count: plan.queues }) },
    { key: "api", included: !!plan.useExternalApi, label: t("featureApi") },
    { key: "schedules", included: !!plan.useSchedules, label: t("featureSchedules") },
    { key: "kanban", included: !!plan.useKanban, label: t("featureKanban") },
    { key: "files", included: !!plan.useFiles, label: t("featureFiles") },
    { key: "internalChat", included: !!plan.useInternalChat, label: t("featureInternalChat") },
    { key: "integrations", included: !!plan.useIntegrations, label: t("featureIntegrations") },
    { key: "negocio", included: !!plan.useNegocio, label: t("featureNegocio") },
    { key: "campaigns", included: !!plan.useCampaigns, label: campaignsLabel },
    { key: "groupCampaigns", included: !!plan.useGroupCampaigns, label: groupsLabel },
    {
      key: "statusCampaigns",
      included: !!plan.useStatusCampaigns,
      label: t("featureStatusCampaigns", { count: Number(plan.maxStatusCampaignsPerDay) || 1 }),
    },
    { key: "openAi", included: !!plan.useOpenAi, label: t("featureOpenAi") },
  ];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  planCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  planHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(2),
    textAlign: "center",
  },
  planPrice: {
    display: "flex",
    justifyContent: "center",
    alignItems: "baseline",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  planFeatures: {
    flex: 1,
    padding: theme.spacing(2),
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.success.main,
    },
  },
  featureExcluded: {
    color: theme.palette.text.disabled,
    "& svg": {
      color: theme.palette.text.disabled,
    },
  },
  planActions: {
    padding: theme.spacing(2),
  },
  infoCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  sectionIntro: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
    },
  },
  priceValue: {
    [theme.breakpoints.down("sm")]: {
      fontSize: "2rem",
      lineHeight: 1.1,
    },
  },
  priceCycle: {
    marginLeft: 8,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginLeft: 0,
      marginTop: 4,
      textAlign: "center",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
}));

const SubscriptionContent = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { list: listPlans, finder: findPlan } = usePlans();

  const numberLocale = useMemo(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") return "en-US";
    if (lang === "pt") return "pt-BR";
    return "es-ES";
  }, [i18n.language]);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [currentPlanName, setCurrentPlanName] = useState(
    () => i18n.t("financial.subscription.noPlanAssigned")
  );
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [paymentConfig, setPaymentConfig] = useState({
    paypalClientId: "",
    stripeSecretKey: "",
    manualPaymentEmail: "",
    manualPaymentWhatsapp: "",
    manualPaymentInstructions: "",
    binanceEnabled: false,
    binancePayId: "",
    binanceQrUrl: "",
  });
  const [binanceInvoiceId, setBinanceInvoiceId] = useState(null);

  useEffect(() => {
    loadPlans();
    loadCurrency();
    loadPaymentConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCurrentPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, user?.companyId, user?.company?.planId, user?.company?.plan?.name]);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const plansList = await listPlans();
      // El plan de prueba no se contrata: suscribirse a él dejaría a una empresa
      // pagada sujeta al pie de prueba y a la purga de datos.
      setPlans(
        (plansList || [])
          .filter((plan) => !isTrialPlanName(plan.name))
          .sort((a, b) => Number(a.value) - Number(b.value))
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentPlan = async () => {
    const embeddedPlanName = user?.company?.plan?.name;
    if (embeddedPlanName) {
      setCurrentPlanName(embeddedPlanName);
      return;
    }

    const planId = user?.company?.planId;
    if (planId) {
      try {
        const plan = await findPlan(planId);
        setCurrentPlanName(plan.name);
        return;
      } catch (err) {
        console.error("Error loading current plan by planId:", err);
      }
    }

    if (user?.companyId) {
      try {
        const { data } = await api.get(`/companies/listPlan/${user.companyId}`);
        if (data?.plan?.name) {
          setCurrentPlanName(data.plan.name);
          return;
        }
      } catch (err) {
        console.error("Error loading current company plan:", err);
      }
    }

    setCurrentPlanName(i18n.t("financial.subscription.noPlanAssigned"));
  };

  const loadCurrency = async () => {
    try {
      const { data } = await api.get("/settings/currency");
      if (data?.value) {
        setCurrency(data.value);
      }
    } catch (err) {
      // Si no existe, usar USD por defecto
      setCurrency("USD");
    }
  };

  const loadPaymentConfig = async () => {
    try {
      const settingKeys = [
        "paypalClientId",
        "stripeSecretKey",
        "manualPaymentEmail",
        "manualPaymentWhatsapp",
        "manualPaymentInstructions",
      ];

      const [responses, binanceRes] = await Promise.all([
        Promise.all(
          settingKeys.map((key) =>
            api.get(`/settings/${key}`).catch((err) => {
              if (err?.response?.status === 404) {
                return { data: null };
              }
              throw err;
            })
          )
        ),
        api.get("/binance-pay/public").catch(() => ({ data: { enabled: false } })),
      ]);

      setPaymentConfig({
        paypalClientId: responses[0]?.data?.value || "",
        stripeSecretKey: responses[1]?.data?.value || "",
        manualPaymentEmail: responses[2]?.data?.value || "",
        manualPaymentWhatsapp: responses[3]?.data?.value || "",
        manualPaymentInstructions: responses[4]?.data?.value || "",
        binanceEnabled: !!binanceRes?.data?.enabled,
        binancePayId: binanceRes?.data?.payId || "",
        binanceQrUrl: binanceRes?.data?.qrUrl || "",
      });
    } catch (err) {
      console.error("Error loading payment config:", err);
    }
  };

  const hasOnlinePayment = Boolean(
    paymentConfig.paypalClientId || paymentConfig.stripeSecretKey || paymentConfig.binanceEnabled
  );
  const hasManualPaymentConfig = Boolean(
    paymentConfig.manualPaymentEmail ||
      paymentConfig.manualPaymentWhatsapp ||
      paymentConfig.manualPaymentInstructions
  );
  const useManualPaymentFallback = !hasOnlinePayment;
  const useBinancePayment = !!paymentConfig.binanceEnabled;
  const normalizedWhatsapp = String(paymentConfig.manualPaymentWhatsapp || "").trim();
  const whatsappLink = normalizedWhatsapp
    ? normalizedWhatsapp.startsWith("http")
      ? normalizedWhatsapp
      : `https://wa.me/${normalizedWhatsapp.replace(/\D/g, "")}`
    : "";

  const handleSelectPlan = async (plan) => {
    if (useManualPaymentFallback) {
      setSelectedInvoice({
        planName: plan.name,
        value: plan.value,
        manualOnly: true,
        manualPaymentEmail: paymentConfig.manualPaymentEmail,
        manualPaymentWhatsapp: paymentConfig.manualPaymentWhatsapp,
        manualPaymentInstructions: paymentConfig.manualPaymentInstructions,
      });
      setSelectedContactId(null);
      setContactModalOpen(true);
      return;
    }

    setCreatingInvoice(true);
    try {
      const { data: invoice } = await api.post("/invoices/create-from-plan", {
        planId: plan.id,
      });

      if (useBinancePayment && !paymentConfig.paypalClientId && !paymentConfig.stripeSecretKey) {
        setBinanceInvoiceId(invoice.id);
        return;
      }

      setSelectedInvoice(invoice);
      setSelectedContactId(null);
      setContactModalOpen(true);
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setSelectedInvoice(null);
    setContactModalOpen(false);
    // Recargar planes después de cerrar el modal
    loadPlans();
    loadCurrentPlan();
  };

  return (
    <>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={selectedInvoice}
        contactId={selectedContactId}
      />
      <InvoiceBinanceCheckoutDialog
        open={!!binanceInvoiceId}
        invoiceId={binanceInvoiceId}
        onClose={() => {
          setBinanceInvoiceId(null);
          loadPlans();
          loadCurrentPlan();
        }}
        onPaid={() => {
          setBinanceInvoiceId(null);
          loadPlans();
          loadCurrentPlan();
        }}
      />

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Información del usuario */}
        <Card className={classes.infoCard} variant="outlined">
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="current-plan"
                  label={i18n.t("financial.subscription.currentPlanLabel")}
                  value={currentPlanName}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {useManualPaymentFallback && (
          <Card className={classes.infoCard} variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {i18n.t("financial.subscription.manualPaymentTitle")}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {i18n.t("financial.subscription.manualPaymentHint")}
              </Typography>
              <Typography variant="body2" paragraph>
                {paymentConfig.manualPaymentInstructions ||
                  i18n.t("financial.subscription.manualDefault")}
              </Typography>
              {paymentConfig.manualPaymentEmail && (
                <Typography variant="body2">
                  {i18n.t("financial.subscription.manualPaymentEmail")}:{" "}
                  <Link href={`mailto:${paymentConfig.manualPaymentEmail}`}>
                    {paymentConfig.manualPaymentEmail}
                  </Link>
                </Typography>
              )}
              {whatsappLink && (
                <Typography variant="body2">
                  WhatsApp:{" "}
                  <Link href={whatsappLink} target="_blank" rel="noopener noreferrer">
                    {paymentConfig.manualPaymentWhatsapp}
                  </Link>
                </Typography>
              )}
              {!hasManualPaymentConfig && (
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("financial.subscription.manualConfigHint")}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Planes disponibles */}
        <Typography variant="h5" gutterBottom style={{ marginTop: 24, marginBottom: 16 }}>
          {i18n.t("financial.subscription.availablePlans")}
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.sectionIntro}>
          {i18n.t("financial.subscription.availablePlansHint")}
        </Typography>

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {plans.map((plan) => (
              <Grid item key={plan.id} xs={12} sm={6} md={4}>
                <Card className={classes.planCard} variant="outlined">
                  <Box className={classes.planHeader}>
                    <Typography variant="h5" component="h2">
                      {plan.name}
                    </Typography>
                  </Box>
                  <CardContent className={classes.planFeatures}>
                    <Box className={classes.planPrice}>
                      <Typography component="h2" variant="h3" color="textPrimary" className={classes.priceValue}>
                        {getCurrencySymbol(currency)}{plan.value.toLocaleString(numberLocale, { minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="h6" color="textSecondary" className={classes.priceCycle}>
                        {i18n.t("financial.subscription.perMonth")}
                      </Typography>
                    </Box>
                    <Box>
                      {getPlanFeatures(plan, numberLocale).map((feature) => (
                        <Box
                          key={feature.key}
                          className={`${classes.featureItem} ${feature.included ? "" : classes.featureExcluded}`}
                        >
                          {feature.included ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                          <Typography variant="body2">{feature.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions className={classes.planActions}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => handleSelectPlan(plan)}
                      disabled={creatingInvoice}
                    >
                      {creatingInvoice ? (
                        <>
                          <CircularProgress size={20} style={{ marginRight: 8 }} />
                          {i18n.t("financial.subscription.processing")}
                        </>
                      ) : (
                        i18n.t("financial.subscription.subscribe")
                      )}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && plans.length === 0 && (
          <Box style={{ textAlign: "center", padding: 40 }}>
            <Typography variant="h6" color="textSecondary">
              {i18n.t("financial.subscription.noPlansTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {i18n.t("financial.subscription.noPlansHint")}
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default SubscriptionContent;
